<template>
  <section class="orbit-detail">
    <button class="orbit-detail__back" type="button" @click="$emit('back')"><i class="el-icon-arrow-left" /> {{ $t('返回机器人') }}</button>
    <header class="orbit-detail__header">
      <div>
        <h1>UPlus-Orbit AI · {{ $t('永续合约') }} {{ number(detail.leverage || 10, 0) }}X</h1>
        <p>OKX · {{ $t('创建时间') }} {{ detail.add_time || '--' }}</p>
        <span class="orbit-detail__status" :class="{ paused: !running }">{{ statusText }}</span>
      </div>
      <div v-if="!historical" class="orbit-detail__actions">
        <button v-if="canOperate" type="button" @click="action('modify')">{{ $t('修改') }}</button>
        <button v-if="canOperate" type="button" @click="action(Number(detail.state) === 1 ? 'pause' : 'resume')">{{ Number(detail.state) === 1 ? $t('暂停') : $t('恢复') }}</button>
        <button class="danger" type="button" @click="action('delete')">{{ $t('删除') }}</button>
      </div>
    </header>

    <div class="orbit-detail__metrics">
      <div><span>{{ $t('投入金额 (USDT)') }}</span><strong>{{ number(detail.space) }}</strong></div>
      <div><span>{{ $t('总收益 (USDT)') }}</span><strong :class="tone(detail.profit_total)">{{ number(detail.profit_total) }}</strong></div>
      <div><span>{{ $t('收益率') }}</span><strong :class="tone(detail.profit_rate)">{{ rate(detail.profit_rate) }}</strong></div>
      <div><span>{{ $t('年化收益率') }}</span><strong :class="tone(detail.profit_rate_year)">{{ rate(detail.profit_rate_year) }}</strong></div>
    </div>

    <article class="orbit-detail__card">
      <header class="orbit-detail__card-header">
        <div class="orbit-detail__tabs" role="tablist">
          <button v-for="tab in tabs" :key="tab.key" type="button" role="tab" :aria-selected="section === tab.key" :class="{ active: section === tab.key }" @click="switchSection(tab.key)">{{ $t(tab.label) }}</button>
        </div>
        <button class="orbit-detail__refresh" type="button" :disabled="loading[section]" @click="load(section)">{{ $t('刷新') }}</button>
      </header>
      <div v-if="errors[section]" class="orbit-detail__error" role="alert">{{ errors[section] }}</div>
      <div v-if="loading[section] && !hasRows" class="orbit-detail__empty"><i class="el-icon-loading" /> {{ $t('加载中…') }}</div>

      <dl v-else-if="section === 'detail'" class="orbit-detail__parameters">
        <div><dt>{{ $t('当前策略') }}</dt><dd>UPlus-Orbit AI</dd></div>
        <div><dt>{{ $t('投入金额 (USDT)') }}</dt><dd>{{ number(detail.space) }}</dd></div>
        <div><dt>{{ $t('交易所余额') }} (USDT)</dt><dd>{{ number(detail.exchange_balance) }}</dd></div>
        <div><dt>{{ $t('完成交易') }}</dt><dd>{{ number(detail.profit_times, 0) }}</dd></div>
        <div><dt>{{ $t('杠杆倍数') }}</dt><dd>{{ number(detail.leverage || 10, 0) }}X</dd></div>
        <div><dt>{{ $t('创建时间') }}</dt><dd>{{ detail.add_time || '--' }}</dd></div>
      </dl>

      <template v-else-if="section === 'positions'">
        <p class="orbit-detail__count">{{ $t('当前仓位') }} · {{ positionTotal }}</p>
        <div v-if="positions.length" class="orbit-detail__records">
          <article v-for="(position, index) in positions" :key="position.instrument_id + ':' + position.direction + ':' + index" class="orbit-detail__record">
            <header>
              <div class="orbit-detail__identity"><strong>{{ position.symbol || '--' }}</strong><span :class="tone(position.side === 'buy' ? 1 : position.side === 'sell' ? -1 : null)">{{ position.side === 'buy' ? $t('做多') : position.side === 'sell' ? $t('做空') : '--' }}</span><small>{{ $t('全仓') }} · {{ number(position.leverage || detail.leverage || 10, 0) }}X</small></div>
              <strong :class="tone(position.profit)">{{ number(position.profit) }} USDT <small>({{ rate(position.profit_rate) }})</small></strong>
            </header>
            <dl>
              <div><dt>{{ $t('持仓数量') }}</dt><dd>{{ number(position.total_size, 8) }}</dd></div>
              <div><dt>{{ $t('初始保证金') }}</dt><dd>{{ number(position.initial_margin, 8) }}</dd></div>
              <div><dt>{{ $t('保证金率') }}</dt><dd>{{ rate(position.margin_ratio) }}</dd></div>
              <div><dt>{{ $t('开仓均价') }}</dt><dd>{{ number(position.avg_price, 8) }}</dd></div>
              <div><dt>{{ $t('标记价格') }}</dt><dd>{{ number(position.mark_price, 8) }}</dd></div>
              <div><dt>{{ $t('预估强平价') }}</dt><dd>{{ number(position.force_price, 8) }}</dd></div>
            </dl>
          </article>
        </div>
        <div v-else class="orbit-detail__empty">{{ $t('暂无当前仓位') }}</div>
      </template>

      <template v-else>
        <p class="orbit-detail__count">{{ $t('当前委托') }} · {{ orderTotal }}</p>
        <div v-if="orders.length" class="orbit-detail__records">
          <article v-for="(order, index) in orders" :key="order.order_id || order.client_order_id || index" class="orbit-detail__record">
            <header>
              <div class="orbit-detail__identity"><strong>{{ order.symbol || '--' }}</strong><span :class="order.side === 'buy' ? 'positive' : order.side === 'sell' ? 'negative' : ''">{{ orderDirection(order) }}</span><small>{{ $t('全仓') }} · {{ number(detail.leverage || 10, 0) }}X</small></div>
              <time>{{ order.entrustTime || '--' }}</time>
            </header>
            <dl>
              <div><dt>{{ $t('委托数量') }}</dt><dd>{{ number(order.size, 8) }}</dd></div>
              <div><dt>{{ $t('已成交数量') }}</dt><dd>{{ number(order.filledSize, 8) }}</dd></div>
              <div><dt>{{ $t('委托价格') }}</dt><dd>{{ number(order.price, 8) }}</dd></div>
              <div><dt>{{ $t('成交均价') }}</dt><dd>{{ number(order.averagePrice, 8) }}</dd></div>
            </dl>
          </article>
        </div>
        <div v-else class="orbit-detail__empty">{{ $t('暂无当前委托') }}</div>
        <footer v-if="orders.length" class="orbit-detail__more">
          <button v-if="nextCursor" type="button" :disabled="loading.orders" @click="load('orders', true)">{{ loading.orders ? $t('加载中…') : $t('加载更多') }}</button>
          <span v-else>{{ $t('已加载全部委托') }}</span>
        </footer>
      </template>
    </article>
  </section>
</template>

<script>
import { loadOrbitStrategyDetail, mergeOrbitDetail, orbitNumber, ORBIT_CURSOR_ERRORS, ORBIT_I18N_KEYS } from '@/api/orbit-strategy'
import { formatNumberUnrounded } from '@/utils/portal-data'

export default {
  name: 'OrbitStrategyDetailPanel',
  props: {
    robot: { type: Object, required: true },
    robotInfo: { type: Object, default: null },
    allRobotInfos: { type: Array, default: () => [] }
  },
  data() {
    return {
      section: 'detail', detail: {}, positions: [], positionTotal: 0,
      orders: [], orderTotal: 0, nextCursor: '',
      loading: { detail: false, positions: false, orders: false },
      errors: { detail: '', positions: '', orders: '' },
      requests: { detail: 0, positions: 0, orders: 0 },
      tabs: [{ key: 'detail', label: '策略参数' }, { key: 'positions', label: '当前仓位' }, { key: 'orders', label: '当前委托' }]
    }
  },
  computed: {
    identity() { return String(this.robot.raw && this.robot.raw.sid || this.robot.id) },
    historical() { return Boolean(this.robot.raw && this.robot.raw.__historyStrategy) },
    canOperate() { return [1, 2].includes(Number(this.detail.state)) },
    powerClosed() {
      const info = this.allRobotInfos.find(item => Number(item.robot_type === undefined ? item.robotType : item.robot_type) === 1) || this.robotInfo
      return info && Number(info.state) === 2
    },
    running() { return !this.historical && !this.powerClosed && Number(this.detail.state) === 1 },
    statusText() {
      if (this.historical) return this.$t('历史策略')
      if (this.powerClosed) return this.$t('已暂停 · 机器人关闭')
      if (this.running) return this.$t('运行中')
      return this.$t(Number(this.detail.state) === 2 ? '已暂停' : '状态未知')
    },
    hasRows() {
      return this.section === 'detail' ? false : this.section === 'positions' ? this.positions.length > 0 : this.orders.length > 0
    }
  },
  watch: {
    identity: {
      immediate: true,
      handler() {
        Object.keys(this.requests).forEach(section => {
          this.requests[section] += 1
          this.loading[section] = false
          this.errors[section] = ''
        })
        this.section = 'detail'
        this.detail = mergeOrbitDetail(this.robot.raw)
        this.positions = []
        this.orders = []
        this.positionTotal = 0
        this.orderTotal = 0
        this.nextCursor = ''
        this.load('detail')
      }
    },
    robot(value) {
      if (value && String(value.raw && value.raw.sid || value.id) === this.identity) this.detail = mergeOrbitDetail(this.detail, value.raw)
    }
  },
  beforeDestroy() {
    Object.keys(this.requests).forEach(section => { this.requests[section] += 1 })
  },
  methods: {
    number(value, decimals = 2) {
      const parsed = orbitNumber(value)
      if (parsed === null) return '--'
      return decimals === 0 ? formatNumberUnrounded(Math.trunc(parsed)) : formatNumberUnrounded(value)
    },
    rate(value) { return orbitNumber(value) === null ? '--' : `${this.number(value)}%` },
    tone(value) { const parsed = orbitNumber(value); return parsed === null ? '' : parsed < 0 ? 'negative' : 'positive' },
    orderDirection(order) {
      if (order.side === 'buy') return this.$t(order.type === 'close' ? '买入平空' : '买入开多')
      if (order.side === 'sell') return this.$t(order.type === 'close' ? '卖出平多' : '卖出开空')
      return '--'
    },
    action(action) { this.$emit('action', { action, robot: { ...this.robot, raw: { ...this.detail } } }) },
    switchSection(section) {
      if (this.section === section) return
      this.section = section
      this.load(section)
    },
    async load(section, append = false) {
      if (append && (this.loading.orders || !this.nextCursor)) return
      const identity = this.identity
      const request = ++this.requests[section]
      this.loading[section] = true
      this.errors[section] = ''
      try {
        const result = await loadOrbitStrategyDetail({ ...this.robot, raw: this.detail }, {
          only: section, pageSize: 10, cursor: append ? this.nextCursor : ''
        })
        if (identity !== this.identity || request !== this.requests[section]) return
        const error = result.sectionErrors[section]
        if (error) {
          if (section === 'orders' && ORBIT_CURSOR_ERRORS.includes(error.code)) {
            this.orders = []
            this.orderTotal = 0
            this.nextCursor = ''
            if (append) {
              this.$emit('toast', { message: this.$tk('robot.orbit_orders_reloaded'), type: 'info' })
              await this.load('orders')
              return
            }
          }
          this.errors[section] = error.i18nKey ? this.$tk(error.i18nKey) : this.$t(error.message)
          return
        }
        if (section === 'detail') {
          this.detail = result.detail
          this.$emit('record', this.robot, result.detail)
        } else if (section === 'positions') {
          this.positions = result.positions
          this.positionTotal = result.positionTotal
        } else {
          this.orders = append ? this.orders.concat(result.orders) : result.orders
          this.orderTotal = result.orderTotal
          this.nextCursor = result.orderNextCursor
        }
      } catch (error) {
        if (identity === this.identity && request === this.requests[section]) this.errors[section] = error.i18nKey ? this.$tk(error.i18nKey) : (error.message ? this.$t(error.message) : this.$tk(ORBIT_I18N_KEYS.dataLoadFailed))
      } finally {
        if (request === this.requests[section]) this.loading[section] = false
      }
    }
  }
}
</script>

<style scoped>
.orbit-detail { min-width: 0; color: #1b2232; }
.orbit-detail button { font-family: inherit; cursor: pointer; }
.orbit-detail__back { border: 0; background: transparent; color: #1f6bff; padding: 0; font-size: 14px; }
.orbit-detail__header { display: flex; justify-content: space-between; gap: 20px; margin: 20px 0 24px; }
.orbit-detail h1 { margin: 0; font-size: 26px; font-weight: 700; overflow-wrap: anywhere; }
.orbit-detail__header p { margin: 9px 0; color: #8b95a9; font-size: 13px; }
.orbit-detail__status { display: inline-block; padding: 4px 10px; border-radius: 20px; color: #008a58; background: #e8f7ef; font-size: 12px; }
.orbit-detail__status.paused { color: #79808f; background: #eef1f6; }
.orbit-detail__actions { display: flex; align-items: center; gap: 8px; }
.orbit-detail__actions button, .orbit-detail__refresh, .orbit-detail__more button { border: 1px solid #dce2ed; border-radius: 8px; background: #fff; color: #1f6bff; padding: 10px 18px; font-size: 13px; white-space: nowrap; }
.orbit-detail__actions .danger { color: #ef4444; border-color: #ffc7c7; }
.orbit-detail__metrics { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); overflow: hidden; border: 1px solid #e7eaf1; border-radius: 14px; background: #fff; margin-bottom: 22px; }
.orbit-detail__metrics > div { min-width: 0; padding: 24px; border-right: 1px solid #f0f2f6; }
.orbit-detail__metrics > div:last-child { border-right: 0; }
.orbit-detail__metrics span { display: block; color: #8b95a9; font-size: 13px; }
.orbit-detail__metrics strong { display: block; margin-top: 8px; font-size: 24px; overflow-wrap: anywhere; }
.orbit-detail__card { border: 1px solid #e7eaf1; border-radius: 14px; background: #fff; overflow: hidden; }
.orbit-detail__card-header { display: flex; justify-content: space-between; gap: 12px; padding: 18px 22px; border-bottom: 1px solid #f0f2f6; }
.orbit-detail__tabs { display: flex; align-items: center; gap: 4px; padding: 3px; border-radius: 9px; background: #f3f5f9; }
.orbit-detail__tabs button { border: 0; border-radius: 7px; background: transparent; color: #79808f; padding: 9px 16px; font-size: 14px; white-space: nowrap; }
.orbit-detail__tabs button.active { color: #1f6bff; background: #fff; }
.orbit-detail__parameters { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 28px; margin: 0; padding: 24px; }
.orbit-detail dt { color: #8b95a9; font-size: 12px; }
.orbit-detail dd { margin: 7px 0 0; color: #3c465b; font-size: 14px; overflow-wrap: anywhere; }
.orbit-detail__count { margin: 20px 22px 0; font-size: 13px; color: #8b95a9; }
.orbit-detail__records { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; padding: 20px 22px; }
.orbit-detail__record { min-width: 0; padding: 18px; border: 1px solid #eef1f7; border-radius: 12px; background: #fbfcfe; }
.orbit-detail__record header { display: flex; flex-wrap: wrap; justify-content: space-between; gap: 12px; }
.orbit-detail__record strong { font-size: 14px; overflow-wrap: anywhere; }
.orbit-detail__identity { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; }
.orbit-detail__record small, .orbit-detail__record time { font-size: 12px; color: #8b95a9; }
.orbit-detail__identity span { font-size: 12px; }
.orbit-detail__record dl { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 18px; margin: 20px 0 0; }
.orbit-detail__empty { min-height: 200px; display: flex; align-items: center; justify-content: center; gap: 8px; color: #8b95a9; font-size: 14px; }
.orbit-detail__error { margin: 18px 22px; padding: 12px; border-radius: 8px; color: #df444b; background: #fff3f3; font-size: 13px; overflow-wrap: anywhere; }
.orbit-detail__more { padding: 0 22px 24px; text-align: center; font-size: 13px; color: #8b95a9; }
.orbit-detail button:disabled { opacity: .6; cursor: wait; }
.orbit-detail .positive { color: #00a866; }
.orbit-detail .negative { color: #ef4444; }
@media (max-width: 760px) {
  .orbit-detail__header { flex-direction: column; gap: 16px; margin: 18px 0; }
  .orbit-detail h1 { font-size: 20px; line-height: 1.5; }
  .orbit-detail__actions button { flex: 1; min-width: 0; }
  .orbit-detail__metrics { grid-template-columns: repeat(2, minmax(0, 1fr)); margin-bottom: 16px; }
  .orbit-detail__metrics > div { padding: 18px 16px; }
  .orbit-detail__metrics > div:nth-child(2) { border-right: 0; }
  .orbit-detail__metrics > div:nth-child(n+3) { border-top: 1px solid #f0f2f6; }
  .orbit-detail__metrics strong { font-size: 21px; }
  .orbit-detail__metrics span { font-size: 12px; }
  .orbit-detail__card-header { flex-wrap: wrap; padding: 14px 12px; }
  .orbit-detail__tabs { flex: 1; min-width: 0; }
  .orbit-detail__tabs button { flex: 1; padding: 9px 7px; font-size: 13px; white-space: normal; }
  .orbit-detail__refresh { margin-left: auto; padding: 8px 12px; }
  .orbit-detail__parameters { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 24px 12px; padding: 20px 16px; }
  .orbit-detail__records { grid-template-columns: minmax(0, 1fr); padding: 16px 12px; gap: 12px; }
  .orbit-detail__record { padding: 16px 12px; }
}
</style>
